import { calculateTimeRange } from '../DatePicker/TimeRangeCalculator';

/**
 * Extract time range from filter values containing duration buckets.
 * Looks for filter values that are JSON strings with duration bucket structure (min, max, unit).
 *
 * @param {Object} filters - Object containing all filter key-value pairs
 * @returns {Object|null} - { startTime, endTime } if duration bucket found, otherwise null
 *
 * @example
 * // Filter with partial connectivity duration bucket
 * const filters = {
 *   "partialConnectivityDuration": ["{\"min\": 1, \"max\": 7, \"unit\": \"days\"}"]
 * };
 * extractTimeRangeFromFilters(filters);
 * // => { startTime: "2026-01-26T...", endTime: "2026-02-01T..." }
 */
export const extractTimeRangeFromFilters = (filters) => {
  if (!filters || typeof filters !== 'object') {
    return null;
  }

  // Iterate through all filter values
  for (const key in filters) {
    const filterValues = filters[key];

    // Check if it's an array (most filter values are arrays)
    if (Array.isArray(filterValues)) {
      for (const value of filterValues) {
        // Try to parse as JSON
        try {
          let parsedValue;
          if (typeof value === 'string') {
            parsedValue = JSON.parse(value);
          } else if (typeof value === 'object') {
            parsedValue = value;
          } else {
            continue;
          }

          // Check if it has duration bucket structure (min/max and unit)
          if (
            parsedValue &&
            typeof parsedValue === 'object' &&
            parsedValue.unit &&
            (parsedValue.min !== undefined || parsedValue.max !== undefined)
          ) {
            // This looks like a duration bucket, calculate time range
            const timeRange = calculateTimeRange(parsedValue);
            if (timeRange.startTime && timeRange.endTime) {
              return timeRange;
            }
          }
        } catch (error) {
          // Not JSON, skip this value
          continue;
        }
      }
    }
  }

  return null;
};

/**
 * Check if a specific filter contains a duration bucket
 *
 * @param {string|Object} filterValue - The filter value to check
 * @returns {boolean} - True if contains duration bucket structure
 */
export const hasDurationBucket = (filterValue) => {
  try {
    let parsed;
    if (typeof filterValue === 'string') {
      parsed = JSON.parse(filterValue);
    } else if (typeof filterValue === 'object') {
      parsed = filterValue;
    } else {
      return false;
    }

    return (
      parsed &&
      typeof parsed === 'object' &&
      parsed.unit &&
      (parsed.min !== undefined || parsed.max !== undefined)
    );
  } catch {
    return false;
  }
};

/**
 * Extract partial connectivity filter parameters for backend API.
 * Uses standardized filter keys: canDataTypes and canDataDateRange.
 * Maps filter IDs to their display values (e.g., "BMS" -> "BMS").
 *
 * @param {Object} filters - Object containing all filter key-value pairs
 * @param {Object} filterConfigs - Filter configuration metadata from page meta
 * @returns {Object} - { canDataTypes: [], canDataDateRange: {...} } or null
 *
 * @example
 * const filters = {
 *   "canDataTypes": ["BMS", "GPS"],
 *   "canDataDateRange": ["{\"min\": 0, \"max\": 7, \"unit\": \"days\"}"]
 * };
 * const filterConfigs = { canDataTypes: { staticData: [{id: "BMS", value: "BMS"}] } };
 * extractPartialConnectivityParams(filters, filterConfigs);
 * // => {
 * //   canDataTypes: ["BMS", "GPS"],
 * //   canDataDateRange: { startDateUTC: "2026-01-27T...", endDateUTC: "2026-02-03T..." }
 * // }
 */
export const extractPartialConnectivityParams = (filters, filterConfigs = null) => {
  if (!filters || typeof filters !== 'object') {
    return null;
  }

  let canDataTypes = null;
  let canDataDateRange = null;

  // Use standardized filter keys directly
  const dataSourceKey = 'canDataTypes';
  const durationKey = 'canDataDateRange';

  // Extract data sources (checkboxes) - IDs are already display values
  if (filters[dataSourceKey] && Array.isArray(filters[dataSourceKey]) && filters[dataSourceKey].length > 0) {
    const selectedIds = filters[dataSourceKey];

    // Map IDs to display values (e.g., "BMS" -> "BMS")
    canDataTypes = selectedIds.map(id => {
      // Try to find the display value from filter config
      if (filterConfigs && filterConfigs[dataSourceKey]) {
        const option = filterConfigs[dataSourceKey].staticData?.find(opt => opt.id === id);
        if (option) {
          return option.value; // Return display value (e.g., "BMS")
        }
      }
      // Fallback: return the ID as-is (already uppercase in your config)
      return id;
    });
  }

  // Extract duration bucket and calculate date range (radio buttons)
  const filterValues = filters[durationKey];
  if (Array.isArray(filterValues)) {
    for (const value of filterValues) {
      try {
        let parsedValue;
        if (typeof value === 'string') {
          parsedValue = JSON.parse(value);
        } else if (typeof value === 'object') {
          parsedValue = value;
        } else {
          continue;
        }

        // Check if it has duration bucket structure
        if (
          parsedValue &&
          typeof parsedValue === 'object' &&
          parsedValue.unit &&
          (parsedValue.min !== undefined || parsedValue.max !== undefined)
        ) {
          const timeRange = calculateTimeRange(parsedValue);
          if (timeRange.endTime) {
            canDataDateRange = {
              endDateUTC: timeRange.endTime,
            };
            // Only include startDateUTC if it exists (not for 30+ days)
            if (timeRange.startTime) {
              canDataDateRange.startDateUTC = timeRange.startTime;
            }
          }
          break;
        }
      } catch (error) {
        continue;
      }
    }
  }

  // Only return if we have at least the date range
  if (canDataDateRange) {
    return {
      canDataTypes,
      canDataDateRange,
    };
  }

  return null;
};

/**
 * Get the filter keys used for partial connectivity (to exclude from regular filters)
 * Uses standardized keys: canDataTypes and canDataDateRange
 *
 * @param {Object} filters - Object containing all filter key-value pairs
 * @returns {Array<string>} - Array of filter keys used for partial connectivity
 */
export const getPartialConnectivityFilterKeys = (filters) => {
  if (!filters || typeof filters !== 'object') {
    return [];
  }

  const usedKeys = [];

  // Check for standardized keys
  if (filters['canDataTypes']) {
    usedKeys.push('canDataTypes');
  }

  if (filters['canDataDateRange']) {
    usedKeys.push('canDataDateRange');
  }

  return usedKeys;
};
